import { sortParam } from '../sort.js';
import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableText, optionalText } from '../common.js';
/**
 * Verträge des Moduls Kontakte (KON-031). Alles, was Kontakte betrifft und von API und Backoffice gemeinsam
 * gebraucht wird, steht in diesem Ordner.
 */
export const CONTACT_KINDS = ['person', 'company'];
export const CONTACT_KIND_LABELS = { person: 'Person', company: 'Firma' };
const optionalEmail = z
    .string()
    .trim()
    .toLowerCase()
    .max(200)
    .optional()
    .transform((v) => (v === '' ? undefined : v))
    .pipe(z.email('Ungültige E-Mail-Adresse').optional());
const nullableEmail = z
    .string()
    .trim()
    .toLowerCase()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('Ungültige E-Mail-Adresse').nullable().optional());
const name = z.string().trim().min(2, 'Name fehlt').max(160);
/** Beziehungsarten des Kontakts (KON-104), ersetzt beim Ändern alle bisherigen. Leer heißt: keine. */
const relationTypeIds = z.array(z.uuid('Ungültige Beziehungsart')).max(20, 'Höchstens 20 Beziehungsarten');
export const createContactInput = z.object({
    kind: z.enum(CONTACT_KINDS, 'Bitte Person oder Firma wählen'),
    name,
    /** Firma, bei der die Person Ansprechpartner ist. Nur für Personen. */
    companyId: z.uuid('Ungültige Firma').optional(),
    email: optionalEmail,
    phone: optionalText(40),
    street: optionalText(120),
    postalCode: optionalText(10),
    city: optionalText(80),
    note: optionalText(5000),
    relationTypeIds: relationTypeIds.default([]),
});
/** Die Art (Person oder Firma) lässt sich nach dem Anlegen nicht ändern, denn Ansprechpartner hängen daran (KON-047) */
export const updateContactInput = z
    .object({
    name: name.optional(),
    /** Firma zuordnen, null löst die Zuordnung. Nur für Personen. */
    companyId: z.uuid('Ungültige Firma').nullable().optional(),
    email: nullableEmail,
    phone: nullableText(40),
    street: nullableText(120),
    postalCode: nullableText(10),
    city: nullableText(80),
    note: nullableText(5000),
    relationTypeIds: relationTypeIds.optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listContactsQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    kind: z.enum(CONTACT_KINDS).optional(),
    /** Nur die Ansprechpartner dieser Firma */
    companyId: z.uuid().optional(),
    /** Nur Kontakte mit dieser Beziehungsart (KON-104) */
    relationTypeId: z.uuid().optional(),
    /** Nur archivierte Kontakte (ausgeblendet, aber nicht gelöscht) */
    archived: z.enum(['true']).optional(),
    sort: sortParam(['name', 'kind', 'email', 'phone', 'city', 'createdAt'], 'name'),
});
/**
 * Vorschläge für vorhandene Kontakte zu einer E-Mail-Adresse und einer Telefonnummer (KON-048). Gesucht wird über die
 * vereinheitlichten Formen, nicht über den Namen, denn gleiche Namen sind noch kein gleicher Kontakt.
 */
export const contactSuggestionsQuery = z
    .object({
    email: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(40).optional(),
    /** Beim Bearbeiten: der Kontakt selbst zählt nicht als Treffer (KON-049) */
    excludeId: z.uuid().optional(),
})
    .refine((v) => !!v.email || !!v.phone, 'E-Mail oder Telefon angeben');
// ---------------------------------------------------------------------------
// Dubletten und Zusammenführen (KON-049)
// ---------------------------------------------------------------------------
/** Felder, bei denen das Büro beim Zusammenführen wählt, welche Seite bleibt */
export const MERGE_FIELDS = ['name', 'companyId', 'email', 'phone', 'street', 'postalCode', 'city', 'note'];
export const MERGE_FIELD_LABELS = {
    name: 'Name',
    companyId: 'Firma',
    email: 'E-Mail',
    phone: 'Telefon',
    street: 'Straße und Hausnummer',
    postalCode: 'Postleitzahl',
    city: 'Ort',
    note: 'Interne Notiz',
};
const side = z.enum(['target', 'source']);
/**
 * Zusammenführen: Der Kontakt in der Adresse (`:id`) bleibt, `sourceId` geht in ihm auf. Je Feld wählt man, von welcher Seite der Wert kommt.
 * Ohne Angabe bleibt der Wert des bleibenden Kontakts, und nur wenn dieser leer ist, kommt der des anderen. Die Notiz lässt sich auch aus beiden zusammensetzen.
 */
export const mergeContactsInput = z
    .object({
    sourceId: z.uuid('Ungültiger Kontakt'),
    choose: z
        .object({
        name: side.optional(),
        companyId: side.optional(),
        email: side.optional(),
        phone: side.optional(),
        street: side.optional(),
        postalCode: side.optional(),
        city: side.optional(),
        note: z.enum(['target', 'source', 'both']).optional(),
    })
        .strict()
        .default({}),
})
    .strict();
// ---------------------------------------------------------------------------
// Vergleichsformen für das Finden vorhandener Kontakte (KON-048, KON-049)
// ---------------------------------------------------------------------------
/** E-Mail-Adresse zum Vergleichen: ohne Leerraum und klein geschrieben. Ohne Angabe null. */
export function normalizeEmail(value) {
    const v = value?.trim().toLowerCase();
    return v ? v : null;
}
/**
 * Telefonnummer zum Vergleichen: nur Ziffern, mit Landesvorwahl. „0151 123456“, „+49 151 123456“, „0049 151 123456“
 * und „+49 (0) 151 123456“ ergeben dasselbe. Nummern ohne führende 0 oder + bleiben, wie sie sind, denn ob eine
 * Vorwahl fehlt, lässt sich nicht erkennen. Als Landesvorwahl gilt Deutschland, solange es keine Einstellung je
 * Mandant gibt. Zu kurze Eingaben ergeben null.
 */
export function normalizePhone(value, countryCode = '49') {
    if (!value)
        return null;
    const plus = value.trim().startsWith('+');
    let digits = value.replace(/\D/g, '');
    if (!digits)
        return null;
    if (!plus) {
        if (digits.startsWith('00'))
            digits = digits.slice(2);
        else if (digits.startsWith('0'))
            digits = countryCode + digits.slice(1);
    }
    // „+49 (0) 151 …“: Die 0 hinter der Landesvorwahl gehört nicht zur Nummer
    if (digits.startsWith(`${countryCode}0`))
        digits = countryCode + digits.slice(countryCode.length + 1);
    return digits.length >= 6 ? digits : null;
}
//# sourceMappingURL=contacts.js.map